import { alignClientDocumentSummaries } from './clientDocumentSummaryAlignment';
import { usePDF, Options } from 'react-to-pdf';

const EXPORT_WIDTH = 1500;
const PAGE_HEIGHT = EXPORT_WIDTH * (210 - 20) / (297 - 20);
const PAGE_BREAK_OFFSET = 1;
const SUMMARY_LABEL = /^(admin fee|already paid|total paid|grand total|deposit(?: paid)?|remaining balance|invoice paid|(?:total )?discount)(?:\b|\s*\()/i;

// html2canvas supplies a detached document. Never alter the interactive screen.
export function prepareClientDocumentPrint(document: Document, root: HTMLElement) {
  root.classList.add('client-pdf-document');
  root.querySelectorAll('[data-document-contract], [data-document-timezone]').forEach(node => node.remove());
  root.querySelectorAll<HTMLElement>('[data-communication-label]').forEach(cell => {
    // html2canvas materializes mobile ::before labels before onclone runs.
    cell.querySelectorAll(':scope > html2canvaspseudoelement').forEach(label => {
      if (label.textContent?.trim() === cell.dataset.communicationLabel) label.remove();
    });
    cell.removeAttribute('data-communication-label');
  });
  const style = document.createElement('style');
  style.textContent = `
    .client-pdf-document { display: block !important; width: ${EXPORT_WIDTH}px !important; min-width: 0 !important; max-width: none !important;
      height: auto !important; margin: 0 !important; padding: 0 !important; background: white !important; }
    .client-pdf-document td::before, .client-pdf-document td::after { content: none !important; display: none !important; }
    .client-pdf-document * { box-sizing: border-box; }
    .client-pdf-document .MuiTableContainer-root { overflow: visible !important; max-height: none !important; }
    .client-pdf-document table { width: 100% !important; min-width: 0 !important; table-layout: fixed !important; border-collapse: collapse !important; }
    .client-pdf-document th, .client-pdf-document td {
      height: auto !important; min-height: 42px !important; padding: 6px 6px 12px !important;
      font-size: 16px !important; line-height: 22px !important; vertical-align: middle !important;
      white-space: normal !important; overflow-wrap: break-word !important; word-break: normal !important; width: auto !important; overflow: visible !important;
    }
    .client-pdf-document td *, .client-pdf-document th * {
      font-size: inherit !important; line-height: inherit !important; white-space: normal !important;
      overflow-wrap: break-word !important; word-break: normal !important; height: auto !important; min-width: 0 !important; width: auto !important; max-width: 100% !important; text-overflow: clip !important;
      max-height: none !important; overflow: visible !important;
    }
    .client-pdf-document .client-pdf-header, .client-pdf-document .client-pdf-footer {
      margin: 0 0 20px !important; padding: 20px !important; gap: 16px !important;
      min-width: 0 !important; max-width: 100% !important;
    }
    .client-pdf-document .client-pdf-footer { margin-bottom: 0 !important; }
    .client-pdf-document .client-pdf-header > *, .client-pdf-document .client-pdf-footer > * {
      min-width: 0 !important; flex-shrink: 1 !important;
    }
    .client-pdf-document .client-pdf-header .MuiTypography-root {
      font-size: 16px !important; line-height: 23px !important; height: auto !important; min-height: 0 !important; max-height: none !important; white-space: normal !important; overflow-wrap: break-word !important; word-break: normal !important;
    }
    .client-pdf-document .client-pdf-header { display: flex !important; flex-direction: row !important; align-items: center !important; gap: 20px !important; padding: 18px 24px 32px !important; }
    .client-pdf-document .client-pdf-header > [data-document-date] { position: absolute !important; right: 24px !important; bottom: 8px !important; }
    .client-pdf-document .client-pdf-header > [data-banner-slot="logo"] { flex: 0 0 160px !important; }
    .client-pdf-document .client-pdf-header > [data-banner-slot="company"] { flex: 1 1 0 !important; }
    .client-pdf-document .client-pdf-header > [data-banner-slot="event"] { flex: 1.3 1 0 !important; }
    .client-pdf-document .client-pdf-header > [data-banner-slot="title"] { flex: 0 0 180px !important; }
    .client-pdf-document .client-pdf-header > * { margin-left: 0 !important; width: auto !important; max-width: 100% !important; }
    .client-pdf-document .client-pdf-header > [data-banner-slot] { padding: 0 !important; }
    .client-pdf-document .client-pdf-header [data-banner-slot="event"] { align-self: center !important; }
    .client-pdf-document .client-pdf-event-pairs { display: flex !important; flex-wrap: wrap !important; gap: 4px 10px !important; }
    .client-pdf-document .client-pdf-event-pairs > * { margin: 0 !important; padding: 0 !important; min-width: 0 !important; }
    .client-pdf-document .client-pdf-event-pairs > .client-pdf-event-label { flex: 0 0 84px !important; text-align: right !important; }
    .client-pdf-document .client-pdf-event-pairs > :not(.client-pdf-event-label) { flex: 0 0 calc(100% - 94px) !important; }
    .client-pdf-document .client-pdf-footer { padding: 14px 24px !important; gap: 20px !important; }
    .client-pdf-document .client-pdf-footer .MuiTypography-root { font-size: 15px !important; line-height: 21px !important; }
    .client-pdf-document .client-pdf-footer .MuiTypography-h3 { font-size: 22px !important; line-height: 28px !important; }
    .client-pdf-document .MuiTableContainer-root { padding-left: 24px !important; padding-right: 24px !important; margin-top: 0 !important; }
    .client-pdf-document [data-client-document-totals] { margin-left: 24px !important; margin-right: 24px !important; }
    .client-pdf-document.client-fixed-document .client-pdf-header,
    .client-pdf-document.client-fixed-document .client-pdf-footer {
      margin-left: 24px !important; margin-right: 24px !important;
      width: calc(100% - 48px) !important;
    }
    .client-pdf-document.client-pdf-document :is(th, td) { padding: 6px 6px 12px !important; }
    .client-pdf-document .client-pdf-spacer.client-pdf-spacer td { padding: 0 !important; }
    .client-pdf-document .client-pdf-header .Toastify { display: none !important; }
    .client-pdf-document .client-pdf-header h1.MuiTypography-root,
    .client-pdf-document .client-pdf-header [data-banner-slot="title"],
    .client-pdf-document .client-pdf-header [data-banner-slot="title"] .MuiTypography-root { font-size: 26px !important; line-height: 32px !important; }
    .client-pdf-document p:not(.client-pdf-header p, .client-pdf-footer p, td p, th p), .client-pdf-document [data-client-document-terms] .MuiTypography-root { padding-bottom: 8px !important; }
    .client-pdf-document .client-pdf-approval { margin: 8px 0 !important; padding: 8px 55px !important; }
    .client-pdf-document .client-pdf-approval hr { margin: 8px 0 !important; }
    .client-pdf-document .client-pdf-signature { height: 65px !important; }
    .client-pdf-document .client-pdf-signature img { max-height: 60px !important; }
    .client-pdf-document .client-pdf-spacer, .client-pdf-document .client-pdf-spacer td {
      min-height: 0 !important; padding: 0 !important; border: 0 !important; font-size: 0 !important; line-height: 0 !important;
      background: white !important;
    }
  `;
  document.head.appendChild(style);

  const quoteWorksheet = root.querySelector<HTMLTableElement>('table[aria-label="Quote shift worksheet"]');
  if (quoteWorksheet) {
    root.querySelectorAll('[data-document-theme-exclude], [data-testid="shared-quote-rate-options"]').forEach(node => node.remove());
    root.querySelectorAll('[data-testid="quote-admin-fee-summary-label"] button').forEach(button => button.remove());
    const banner = root.querySelector<HTMLElement>('[data-document-banner]');
    if (banner) { banner.style.setProperty('margin', '0', 'important'); banner.style.setProperty('width', '100%', 'important'); }
    let parent = quoteWorksheet.parentElement;
    while (parent && parent !== root) {
      parent.style.setProperty('margin-left', '0', 'important');
      parent.style.setProperty('margin-right', '0', 'important');
      parent.style.setProperty('width', '100%', 'important');
      parent = parent.parentElement;
    }
  }

  // Preserve values if an export is requested while an inline field is open.
  root.querySelectorAll('input, textarea, select').forEach((field) => {
    const text = document.createElement('span');
    if (field.tagName === 'INPUT' && ['checkbox', 'radio', 'hidden'].includes((field as HTMLInputElement).type)) {
      field.remove();
      return;
    }
    text.textContent = (field as HTMLInputElement).value;
    field.replaceWith(text);
  });
  root.querySelectorAll('button, [role="button"], .MuiSvgIcon-root, [data-html2canvas-ignore="true"], [data-pdf-exclude]').forEach((node) => node.remove());
  root.querySelectorAll('tr').forEach((row) => {
    if (!row.textContent?.trim() && !row.querySelector('img, canvas')) row.remove();
  });
  root.querySelectorAll('thead th').forEach((cell) => {
    cell.textContent = cell.textContent?.replace(/^Actions\s*\/\s*/i, '') ?? '';
  });

  const brandedBlocks = Array.from(root.children).filter((node) => node.querySelector('[data-client-document-logo], img') && !node.querySelector('table'));
  (root.querySelector('[data-document-header]') ?? brandedBlocks[0])?.classList.add('client-pdf-header');
  if (brandedBlocks.length > 1) brandedBlocks[brandedBlocks.length - 1].classList.add('client-pdf-footer');

  // html2canvas does not implement object-fit. Resolve the actual image ratio
  // into pixel dimensions instead of allowing a CSS width/max-height mismatch.
  root.querySelectorAll<HTMLImageElement>('.client-pdf-header img, .client-pdf-footer img').forEach((logo) => {
    if (!logo.naturalWidth || !logo.naturalHeight) return;
    const inHeader = Boolean(logo.closest('.client-pdf-header'));
    const selectedSize = parseFloat(document.defaultView?.getComputedStyle(logo).getPropertyValue('--document-logo-size') || '192');
    const bound = inHeader ? Math.min(160, selectedSize * 0.75) : 64;
    const scale = Math.min(bound / logo.naturalWidth, bound / logo.naturalHeight);
    logo.style.setProperty('width', `${logo.naturalWidth * scale}px`, 'important');
    logo.style.setProperty('height', `${logo.naturalHeight * scale}px`, 'important');
    logo.style.setProperty('max-width', 'none', 'important');
    logo.style.setProperty('max-height', 'none', 'important');
    logo.style.setProperty('aspect-ratio', 'auto', 'important');
  });

  // Pair the existing label/value columns so a wrapped value cannot shift the
  // remaining labels out of alignment. Retain all original nodes and values.
  root.querySelectorAll<HTMLElement>('.client-pdf-header [data-banner-slot="event"]').forEach((event) => {
    const pairedRows = Array.from(event.children).filter(row => row.hasAttribute('data-quote-detail-row'));
    if (pairedRows.length) {
      const pairs = pairedRows.map(row => Array.from(row.children));
      event.replaceChildren();
      event.classList.add('client-pdf-event-pairs');
      pairs.forEach(([label, value]) => {
        label.classList.add('client-pdf-event-label');
        event.append(label, value);
      });
      return;
    }
    const [labels, values] = Array.from(event.children);
    if (event.children.length !== 2 || !labels.children.length || labels.children.length !== values.children.length) return;
    const pairs = Array.from(labels.children).map((label, index) => [label, values.children[index]]);
    event.replaceChildren();
    event.classList.add('client-pdf-event-pairs');
    pairs.forEach(([label, value]) => {
      label.classList.add('client-pdf-event-label');
      event.append(label, value);
    });
  });

  // Keep the original cells, alignment and column spans. Mark consecutive
  // financial rows only so pagination can keep their existing layout together.
  root.querySelectorAll('tr').forEach(row => {
    const cells = Array.from(row.cells).filter(cell => cell.textContent?.trim());
    if (cells.length === 2 && SUMMARY_LABEL.test(cells[0].textContent!.trim())) row.classList.add('client-pdf-summary-row');
  });

  root.querySelectorAll('table').forEach((table) => {
    const headers = Array.from(table.querySelectorAll('thead th'));
    if (!headers.length) return;
    const weights: Record<string, number> = { date: 105, qty: 50, position: 95, 'call time': 90, notes: 125, 'rate type': 95, 'call type': 55, 'meal break': 80, rate: 65, 'st hrs': 45, 'ot hrs': 45, 'dt hrs': 45, totals: 90 };
    const sizes = headers.map((cell) => weights[cell.textContent!.trim().toLowerCase()] ?? 70);
    const total = sizes.reduce((sum, size) => sum + size, 0);
    const group = document.createElement('colgroup');
    sizes.forEach((size) => { const col = document.createElement('col'); col.style.width = `${size / total * 100}%`; group.append(col); });
    // A second colgroup adds another set of columns; replace the interactive
    // worksheet's sizing rather than combining incompatible column models.
    Array.from(table.children).filter(child => child.tagName === 'COLGROUP').forEach(child => child.remove());
    table.prepend(group);
    // Editor cells reserve almost all spare width for Notes. Inline print-only
    // overrides outrank those important component rules without changing the UI.
    table.querySelectorAll<HTMLElement>('th, td').forEach(cell => {
      cell.style.setProperty('width', 'auto', 'important');
      cell.style.setProperty('min-width', '0', 'important');
      cell.style.setProperty('max-width', 'none', 'important');
    });
  });

  // Give the raster renderer discrete, unbroken text runs. Its word wrapping
  // can overlap glyphs when a single text node wraps across narrow columns.
  root.querySelectorAll<HTMLElement>('th, td, .client-pdf-header .MuiTypography-root, span[data-client-document-logo]').forEach((cell) => {
    if (cell.querySelector('img, canvas, table')) return;
    // These totals already have independent label/value spacing; flattening
    // them into a single text run would discard that layout in the PDF.
    if (cell.querySelector('[data-document-independent-summary]')) return;
    // textContent drops authored <br> boundaries. Preserve them before creating
    // the renderer's unbroken runs; never reflow the interactive document.
    const text = cell.cloneNode(true) as HTMLElement;
    text.querySelectorAll('br').forEach(br => br.replaceWith('\u2028'));
    const value = text.textContent ?? '';
    const computed = document.defaultView?.getComputedStyle(cell);
    const available = Math.max(24, cell.getBoundingClientRect().width - parseFloat(computed?.paddingLeft || '0') - parseFloat(computed?.paddingRight || '0'));
    const context = document.defaultView && 'CanvasRenderingContext2D' in document.defaultView
      ? document.createElement('canvas').getContext('2d') : null;
    if (context && computed) context.font = `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;
    cell.replaceChildren();
    const atomic = /^(?:\d{1,2}:\d{2}\s*[AP]M|\d{4}-\d{2}-\d{2})$/;
    const runs = value.split(/(\d{1,2}:\d{2}\s*[AP]M|\d{4}-\d{2}-\d{2})/).flatMap(part =>
      atomic.test(part) ? [part] : part.split(/(\s+|(?<=[/-]))/).filter(Boolean));
    runs.forEach((word) => {
      if (/^\s+$/.test(word)) {
        word.split('\u2028').forEach((space, index) => {
          if (index) cell.append(document.createElement('br'));
          if (space) cell.append(document.createTextNode(space));
        });
        return;
      }
      const chunks: string[] = [];
      let chunk = '';
      for (const character of word) {
        const candidate = chunk + character;
        const width = context ? context.measureText(candidate).width : candidate.length * 8;
        if (chunk && width > available - 4) { chunks.push(chunk); chunk = character; }
        else chunk = candidate;
      }
      if (chunk) chunks.push(chunk);
      for (const part of chunks) {
        const run = document.createElement('span');
        for (const character of part) {
          const glyph = document.createElement('span');
          glyph.textContent = character;
          glyph.style.setProperty('display', 'inline-block', 'important');
          glyph.style.setProperty('white-space', 'pre', 'important');
          run.append(glyph);
        }
        run.style.setProperty('display', 'inline-block', 'important');
        run.style.setProperty('white-space', 'pre', 'important');
        cell.append(run);
      }
    });
  });

  const signature = root.querySelector('img[alt="Client Signature"]');
  if (signature) {
    let approval = signature.parentElement;
    while (approval && approval.parentElement !== root) approval = approval.parentElement;
    approval?.classList.add('client-pdf-approval');
    signature.parentElement?.classList.add('client-pdf-signature');
  }

  // Start the final date group after the preceding crew subtotal. Only keep it
  // with its financial summary when the group plus a repeated header fits.
  const finalGroups = new Map<HTMLElement, HTMLElement>();
  const groupedRows = new Set<Element>();
  root.querySelectorAll('tbody').forEach(body => {
    const summaries = Array.from(body.querySelectorAll<HTMLElement>(':scope > .client-pdf-summary-row'));
    const table = body.closest('table');
    const tableContainer = table?.parentElement?.matches('.MuiTableContainer-root') ? table.parentElement : table;
    const adjacentSummary = tableContainer?.nextElementSibling;
    const externalSummary = adjacentSummary?.matches('[data-client-document-totals].client-pdf-summary') ? adjacentSummary as HTMLElement : null;
    const firstSummary = summaries[0] || externalSummary;
    const subtotal = summaries.length ? firstSummary?.previousElementSibling : body.lastElementChild;
    const isSubtotal = (row: Element | null) => Boolean(row && !row.matches('.client-pdf-summary-row') &&
      (row.matches('[data-document-total="subtotal"]') || /^(?:crew sub\s?total|sub\s?total)\b/i.test(row.textContent?.trim() || '')));
    if (!firstSummary || !isSubtotal(subtotal)) return;
    let first = subtotal as HTMLElement;
    while (first.previousElementSibling?.tagName === 'TR' && !isSubtotal(first.previousElementSibling)) {
      first = first.previousElementSibling as HTMLElement;
    }
    if (first === subtotal) return;
    let last = firstSummary;
    while (last.nextElementSibling?.matches('.client-pdf-summary-row')) last = last.nextElementSibling as HTMLElement;
    const height = last.getBoundingClientRect().bottom - first.getBoundingClientRect().top;
    const headerHeight = first.closest('table')?.querySelector('thead')?.getBoundingClientRect().height || 0;
    if (height + headerHeight + PAGE_BREAK_OFFSET >= PAGE_HEIGHT) return;
    finalGroups.set(first, last);
    groupedRows.add(last);
    for (let row: Element | null = first; row; row = row.nextElementSibling) {
      groupedRows.add(row);
      if (row === last) break;
    }
  });

  // The raster exporter slices at fixed page heights. Move complete rows and
  // branded blocks past those cuts so borders/text do not straddle pages.
  root.querySelectorAll<HTMLElement>('tr, p, .MuiTypography-root, [aria-label="Document contract terms and notes"], .client-pdf-header, .client-pdf-footer, .client-pdf-approval, .client-pdf-summary').forEach((block) => {
    const row = block.closest('tr');
    if ((row && row !== block) || (block.matches('.MuiTypography-root') && block.querySelector('.MuiTypography-root'))) return;
    if ((groupedRows.has(block) || (row && groupedRows.has(row))) && !finalGroups.has(block)) return;
    const group = block.closest('.client-pdf-summary, .client-pdf-header, .client-pdf-footer, .client-pdf-approval');
    if (group && group !== block) return;
    const top = block.getBoundingClientRect().top - root.getBoundingClientRect().top;
    let height = block.getBoundingClientRect().height;
    let groupEnd = finalGroups.get(block) || block;
    if (finalGroups.has(block)) height = groupEnd.getBoundingClientRect().bottom - block.getBoundingClientRect().top;
    if (block.matches('.client-pdf-summary-row')) {
      if (block.previousElementSibling?.matches('.client-pdf-summary-row')) return;
      while (groupEnd.nextElementSibling?.matches('.client-pdf-summary-row')) groupEnd = groupEnd.nextElementSibling as HTMLElement;
      height = groupEnd.getBoundingClientRect().bottom - block.getBoundingClientRect().top;
    }
    const next = block.nextElementSibling;
    // Do not orphan a crew subtotal from its final shift.
    if (block.tagName === 'TR' && !finalGroups.has(block) && !block.matches('.client-pdf-summary-row') && next?.matches('[data-document-total="subtotal"]')) {
      height = next.getBoundingClientRect().bottom - block.getBoundingClientRect().top;
    }
    // Keep the final totals or terms with the banner when only whitespace separates them.
    if (finalGroups.has(block) || block.matches('.client-pdf-summary, .client-pdf-summary-row, p, .MuiTypography-root')) {
      const footer = root.querySelector<HTMLElement>('.client-pdf-footer');
      if (footer && (block.compareDocumentPosition(footer) & 4)) {
        const between = document.createRange();
        between.setStartAfter(groupEnd);
        between.setEndBefore(footer);
        if (!between.toString().trim()) {
          const withFooter = footer.getBoundingClientRect().bottom - block.getBoundingClientRect().top;
          const headerHeight = block.closest('table')?.querySelector('thead')?.getBoundingClientRect().height || 0;
          // Preserve the existing footer pairing without making a fitting final
          // date group too tall to paginate together.
          if (!finalGroups.has(block) || withFooter + headerHeight + PAGE_BREAK_OFFSET < PAGE_HEIGHT) height = withFooter;
        }
      }
    }
    const remaining = PAGE_HEIGHT - (top % PAGE_HEIGHT);
    if (height <= remaining || height >= PAGE_HEIGHT) return;
    const spacer = document.createElement(block.tagName === 'TR' ? 'tr' : 'div');
    spacer.className = 'client-pdf-spacer';
    spacer.style.setProperty('height', `${remaining + PAGE_BREAK_OFFSET}px`, 'important');
    if (block.tagName === 'TR') {
      const cell = document.createElement('td');
      cell.colSpan = Array.from((block as HTMLTableRowElement).cells).reduce((total, item) => total + item.colSpan, 0);
      cell.style.setProperty('height', `${remaining + PAGE_BREAK_OFFSET}px`, 'important');
      spacer.appendChild(cell);
    }
    block.before(spacer);
    if (block.tagName === 'TR' && !block.closest('thead')) {
      const header = block.closest('table')?.querySelector('thead');
      if (header && height + header.getBoundingClientRect().height < PAGE_HEIGHT) {
        Array.from(header.rows).forEach((row) => {
          const repeated = row.cloneNode(true) as HTMLTableRowElement;
          repeated.classList.add('client-pdf-repeated-header');
          Array.from(repeated.cells).forEach((cell, index) => {
            const appearance = document.defaultView?.getComputedStyle(row.cells[index]);
            cell.style.setProperty('background', appearance?.backgroundColor || 'white', 'important');
            cell.style.setProperty('color', appearance?.color || 'black', 'important');
          });
          block.before(repeated);
        });
      }
    }
  });
  alignClientDocumentSummaries(root);
}

export function useClientDocumentPDF(options: Options) {
  return usePDF({
    ...options,
    page: { format: 'A4', orientation: 'landscape', margin: 10 },
    overrides: { canvas: { windowWidth: 1600, scrollX: 0, scrollY: 0, onclone: prepareClientDocumentPrint } }
  });
}
